import type { BaseRuntimeConfig, IBaseEnvironmentClientAlpha, ViteMetaEnv } from '@blufin/web';
import { ReactEnvironmentClientAlpha } from '@blufin/web';
import type { ReactNode } from 'react';
import React, { createContext, useRef } from 'react';

export interface EnvironmentProviderProps<TCustom extends object = {}> {
  children: ReactNode;
  runtimeConfig: BaseRuntimeConfig & TCustom;
  viteMetaEnv: ViteMetaEnv;
}

export const EnvironmentContextAlpha = createContext<IBaseEnvironmentClientAlpha | null>(null);

/**
 * Provider component for environment context.
 * @param root0 - Component props.
 * @param root0.viteMetaEnv - The Vite meta environment object containing environment variables.
 * @param root0.runtimeConfig - The runtime configuration loaded from config.json
 * @param root0.children - React child elements to be wrapped by the provider.
 */
export const EnvironmentProviderAlpha: React.FC<EnvironmentProviderProps> = ({
  viteMetaEnv,
  runtimeConfig,
  children
}) => {
  const environmentClientRef = useRef<InstanceType<typeof ReactEnvironmentClientAlpha> | null>(null);
  if (!environmentClientRef.current)
    environmentClientRef.current = new ReactEnvironmentClientAlpha(viteMetaEnv, runtimeConfig);
  return (
    <EnvironmentContextAlpha.Provider value={environmentClientRef.current}>{children}</EnvironmentContextAlpha.Provider>
  );
};
